import React, { useState, useEffect } from 'react';
import {
  getInstagramSettings,
  saveInstagramSettings,
  saveInstagramColumns,
  getInstagramFeed,
  refreshInstagramFeed,
  connectInstagram,
  disconnectInstagram,
  InstagramSettings,
  InstagramPost,
  InstagramFeedResponse
} from '../../utils/InstagramAPI';
import './InstagramManagement.css';

const COLUMN_OPTIONS = [2, 3, 4, 5, 6];
const POST_OPTIONS = [3, 4, 5, 6, 8, 9, 10, 12];

const InstagramManagement: React.FC = () => {
  const [settings, setSettings] = useState<InstagramSettings | null>(null);
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [columns, setColumns] = useState(4);
  const [displayLimit, setDisplayLimit] = useState(12);
  const [savingColumns, setSavingColumns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showManualConfig, setShowManualConfig] = useState(false);
  const [manualConfig, setManualConfig] = useState({
    accessToken: '',
    accountId: '',
    postsLimit: 12,
    refreshInterval: 3600
  });
  const [feedInfo, setFeedInfo] = useState<{ cached: boolean; connected: boolean } | null>(null);

  // Check for OAuth callback success/error
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('instagram_connected');
    const error = urlParams.get('instagram_error');
    
    if (connected === 'true') {
      setSuccess('Instagram account connected successfully!');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      loadSettings();
      loadFeed();
    } else if (error) {
      setError(`Connection failed: ${error}`);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Load settings and feed on mount
  useEffect(() => {
    loadSettings();
    loadFeed();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getInstagramSettings();
      setSettings(data);
      setColumns(data.columns || 4);
      setDisplayLimit(data.displayLimit || data.postsLimit || 12);
      setManualConfig({
        accessToken: '',
        accountId: data.accountId || '',
        postsLimit: data.postsLimit || 12,
        refreshInterval: data.refreshInterval || 3600
      });
    } catch (error: any) {
      console.error('Error loading Instagram settings:', error);
      setError('Failed to load Instagram settings');
    } finally {
      setLoading(false);
    }
  };

  const loadFeed = async () => {
    try {
      const data: InstagramFeedResponse = await getInstagramFeed();
      setPosts(data.posts || []);
      setFeedInfo({ cached: data.cached, connected: data.connected });
      if (data.warning) {
        setError(data.warning);
      }
    } catch (error: any) {
      console.error('Error loading Instagram feed:', error);
      // Don't set error here, just log it
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      const { authUrl } = await connectInstagram();
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Error connecting Instagram:', error);
      setError(error.message || 'Failed to initiate Instagram connection');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Instagram account?')) {
      return;
    }

    try {
      setError(null);
      await disconnectInstagram();
      setSettings(null);
      setPosts([]);
      setFeedInfo(null);
      setSuccess('Instagram account disconnected successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error disconnecting Instagram:', error);
      setError(error.message || 'Failed to disconnect Instagram account');
    }
  };

  const handleManualSave = async () => {
    if (!manualConfig.accessToken || !manualConfig.accountId) {
      setError('Access token and Account ID are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await saveInstagramSettings({
        accessToken: manualConfig.accessToken,
        accountId: manualConfig.accountId,
        postsLimit: manualConfig.postsLimit,
        refreshInterval: manualConfig.refreshInterval,
        columns
      });
      setSuccess('Instagram settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      await loadSettings();
      await loadFeed();
      setShowManualConfig(false);
    } catch (error: any) {
      console.error('Error saving Instagram settings:', error);
      setError(error.message || 'Failed to save Instagram settings');
    } finally {
      setSaving(false);
    }
  };

  const saveLayout = async (
    patch: { columns?: number; displayLimit?: number },
    message: string
  ) => {
    if (!settings?.connected || savingColumns) return;
    const nextColumns = patch.columns ?? columns;
    const nextLimit = patch.displayLimit ?? displayLimit;
    if (nextColumns === columns && nextLimit === displayLimit) return;
    const previousColumns = columns;
    const previousLimit = displayLimit;
    setColumns(nextColumns);
    setDisplayLimit(nextLimit);
    try {
      setSavingColumns(true);
      setError(null);
      const saved = await saveInstagramColumns(patch.columns, patch.displayLimit);
      setColumns(saved.columns);
      if (typeof saved.displayLimit === 'number') {
        setDisplayLimit(saved.displayLimit);
      }
      setSuccess(message);
      setTimeout(() => setSuccess(null), 3000);
    } catch (layoutError: any) {
      setColumns(previousColumns);
      setDisplayLimit(previousLimit);
      setError(layoutError.message || 'Failed to save the Instagram layout');
    } finally {
      setSavingColumns(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const result = await refreshInstagramFeed();
      setPosts(result.posts);
      setSuccess(`Refreshed ${result.count} posts successfully!`);
      setTimeout(() => setSuccess(null), 3000);
      await loadSettings();
    } catch (error: any) {
      console.error('Error refreshing Instagram feed:', error);
      setError(error.message || 'Failed to refresh Instagram feed');
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <section className="admin-section">
        <div className="section-header">
          <h2>Instagram</h2>
          <p>Connect your Instagram account for automatic feed updates</p>
        </div>
        <div className="section-content">
          <p>Loading...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-section">
      <div className="section-header">
        <h2>Instagram</h2>
        <p>Connect your Instagram account for automatic feed updates</p>
      </div>

      {error && (
        <div className="admin-alert admin-alert-error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button className="alert-close" onClick={() => setError(null)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {success && (
        <div className="admin-alert admin-alert-success">
          <i className="fas fa-check-circle"></i>
          {success}
          <button className="alert-close" onClick={() => setSuccess(null)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="section-content">
        {/* Connection Status */}
        <div className="instagram-status-card">
          <h3>Connection Status</h3>
          {settings?.connected ? (
            <div className="status-connected">
              <div className="status-header">
                <i className="fas fa-check-circle status-icon connected"></i>
                <div>
                  <strong>Connected</strong>
                  {settings.username && (
                    <p className="username">@{settings.username}</p>
                  )}
                </div>
                {settings.profilePicture && (
                  <img 
                    src={settings.profilePicture} 
                    alt="Instagram profile" 
                    className="profile-picture"
                  />
                )}
              </div>
              {settings.lastRefresh && (
                <p className="last-refresh">
                  Last refreshed: {formatDate(settings.lastRefresh)}
                </p>
              )}
              <div className="status-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  <i className="fas fa-sync-alt"></i>
                  {refreshing ? 'Refreshing...' : 'Refresh Feed'}
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={handleDisconnect}
                >
                  <i className="fas fa-unlink"></i>
                  Disconnect
                </button>
              </div>
            </div>
          ) : (
            <div className="status-disconnected">
              <i className="fas fa-times-circle status-icon disconnected"></i>
              <p><strong>Not Connected</strong></p>
              <p>Connect your Instagram Business account to display your posts on the website.</p>
              <div className="connection-options">
                <button 
                  className="btn btn-primary" 
                  onClick={handleConnect}
                >
                  <i className="fab fa-instagram"></i>
                  Connect via OAuth
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowManualConfig(!showManualConfig)}
                >
                  <i className="fas fa-cog"></i>
                  Manual Configuration
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Manual Configuration */}
        {showManualConfig && !settings?.connected && (
          <div className="instagram-config-card">
            <h3>Manual Configuration</h3>
            <p className="config-note">
              Enter your Instagram API credentials manually. You can get these from the{' '}
              <a href="/INSTAGRAM_SETUP_GUIDE.md" target="_blank" rel="noopener noreferrer">
                setup guide
              </a>.
            </p>
            <div className="form-group">
              <label>
                Access Token (Long-lived)
                <input
                  type="text"
                  value={manualConfig.accessToken}
                  onChange={(e) => setManualConfig({ ...manualConfig, accessToken: e.target.value })}
                  placeholder="Enter your long-lived access token"
                  className="form-control"
                />
              </label>
            </div>
            <div className="form-group">
              <label>
                Instagram Business Account ID
                <input
                  type="text"
                  value={manualConfig.accountId}
                  onChange={(e) => setManualConfig({ ...manualConfig, accountId: e.target.value })}
                  placeholder="Enter your Instagram Business Account ID"
                  className="form-control"
                />
              </label>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  Posts Limit
                  <input
                    type="number"
                    value={manualConfig.postsLimit}
                    onChange={(e) => setManualConfig({ ...manualConfig, postsLimit: parseInt(e.target.value) || 12 })}
                    min="1"
                    max="50"
                    className="form-control"
                  />
                </label>
              </div>
              <div className="form-group">
                <label>
                  Refresh Interval (seconds)
                  <input
                    type="number"
                    value={manualConfig.refreshInterval}
                    onChange={(e) => setManualConfig({ ...manualConfig, refreshInterval: parseInt(e.target.value) || 3600 })}
                    min="300"
                    step="300"
                    className="form-control"
                  />
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button 
                className="btn btn-primary" 
                onClick={handleManualSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowManualConfig(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="instagram-layout-card">
          <h3>Homepage feed</h3>
          <p className="config-note">
            Choose how many posts appear, and how many sit in each row on a large screen.
            Phones and tablets use fewer columns so the photos stay large.
          </p>
          <p className="config-note">Posts</p>
          <div className="instagram-column-options" role="group" aria-label="Instagram posts">
            {POST_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`instagram-column-option ${displayLimit === option ? 'is-selected' : ''}`}
                disabled={savingColumns || !settings?.connected}
                aria-pressed={displayLimit === option}
                onClick={() => saveLayout({ displayLimit: option }, `The homepage now shows ${option} Instagram posts.`)}
              >
                {option}
              </button>
            ))}
          </div>
          <p className="config-note">Columns</p>
          <div className="instagram-column-options" role="group" aria-label="Instagram columns">
            {COLUMN_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`instagram-column-option ${columns === option ? 'is-selected' : ''}`}
                disabled={savingColumns || !settings?.connected}
                aria-pressed={columns === option}
                onClick={() => saveLayout({ columns: option }, `The Instagram feed now uses ${option} columns on a large screen.`)}
              >
                {option}
              </button>
            ))}
          </div>
          {!settings?.connected && (
            <p className="config-note">Connect Instagram to save this layout.</p>
          )}
        </div>

        {/* Feed Preview */}
        {settings?.connected && (
          <div className="instagram-feed-preview">
            <div className="preview-header">
              <h3>Feed Preview</h3>
              {feedInfo && (
                <span className={`feed-status ${feedInfo.cached ? 'cached' : 'fresh'}`}>
                  <i className={`fas fa-${feedInfo.cached ? 'database' : 'cloud'}`}></i>
                  {feedInfo.cached ? 'Cached' : 'Fresh'}
                </span>
              )}
            </div>
            {posts.length > 0 ? (
              <div className="posts-grid" data-columns={columns}>
                {posts.slice(0, displayLimit).map((post) => (
                  <div key={post.id} className="post-card">
                    <a 
                      href={post.permalink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="post-link"
                    >
                      <div className="post-image-container">
                        <img 
                          src={post.mediaUrl} 
                          alt={post.caption.substring(0, 50) || 'Instagram post'} 
                          className="post-image"
                        />
                        <div className="post-overlay">
                          <div className="post-stats">
                            <span><i className="fas fa-heart"></i> {post.likeCount}</span>
                            <span><i className="fas fa-comment"></i> {post.commentsCount}</span>
                          </div>
                        </div>
                      </div>
                      {post.caption && (
                        <p className="post-caption" title={post.caption}>
                          {post.caption.length > 100 
                            ? post.caption.substring(0, 100) + '...' 
                            : post.caption}
                        </p>
                      )}
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <i className="fas fa-images"></i>
                <p>No posts found. Try refreshing the feed.</p>
              </div>
            )}
          </div>
        )}

        {/* Settings Info */}
        {settings?.connected && (
          <div className="instagram-settings-info">
            <h3>Current Settings</h3>
            <div className="settings-grid">
              <div className="setting-item">
                <strong>Columns:</strong> {columns}
              </div>
              <div className="setting-item">
                <strong>Posts shown:</strong> {displayLimit}
              </div>
              <div className="setting-item">
                <strong>Posts fetched:</strong> {settings.postsLimit || 12}
              </div>
              <div className="setting-item">
                <strong>Refresh Interval:</strong> {settings.refreshInterval ? `${settings.refreshInterval / 60} minutes` : '1 hour'}
              </div>
              <div className="setting-item">
                <strong>Account ID:</strong> {settings.accountId || 'Not set'}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default InstagramManagement;


